// src/components/settings/catalog/catalogUiParts.js
// CATALOG SCREEN VERIFIER: catscreen-7a2c4e91 — Settings › Catalog, Stage 2.
//
// Shared looks for the Catalog screen (same purple and shapes as Settings › Taxes). Every
// button and input is at least 44px tall so it's easy to tap on a phone.
import React from 'react';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';

const BTN = 'inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5F4EC9]/40';
export const BTN_GHOST = `${BTN} border-gray-200 text-gray-700 bg-white hover:bg-gray-50`;
export const BTN_PRIMARY = `${BTN} border-[#5F4EC9] text-white bg-[#5F4EC9] hover:bg-[#4f40b0]`;
export const BTN_DANGER = `${BTN} border-gray-200 text-red-700 bg-white hover:bg-red-50`;
export const BTN_LINK = 'inline-flex items-center gap-1 min-h-[44px] px-1 text-sm font-medium text-[#5F4EC9] hover:underline disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5F4EC9]/40 rounded';
export const INPUT = 'w-full min-h-[44px] px-3 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]/30 disabled:bg-gray-50 disabled:text-gray-500';
export const LABEL = 'block text-sm font-medium text-gray-700 mb-1';

export function Req() {
    return <span className="ml-1 text-xs font-semibold text-red-600">Required</span>;
}
export function Opt() {
    return <span className="ml-1 text-xs font-normal text-gray-400">Optional</span>;
}

export function ErrorLine({ text, onRetry }) {
    if (!text) return null;
    return (
        <div role="alert" className="flex flex-wrap items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
            <AlertTriangle size={16} className="shrink-0" />
            <span className="flex-1 min-w-[12rem]">{text}</span>
            {onRetry && <button type="button" className={BTN_GHOST} onClick={onRetry}>Try again</button>}
        </div>
    );
}

export function Notice({ notice, onClose }) {
    if (!notice) return null;
    return (
        <div role="status" className="flex flex-wrap items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-900">
            <CheckCircle2 size={16} className="shrink-0" />
            <span className="flex-1 min-w-[12rem]">{notice.text}</span>
            {notice.action && (
                <button type="button" className="min-h-[44px] px-3 font-semibold underline disabled:opacity-50" disabled={notice.busy} onClick={notice.action.run}>
                    {notice.action.label}
                </button>
            )}
            <button type="button" className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-green-700 rounded" aria-label="Dismiss" onClick={onClose}>
                <X size={16} />
            </button>
        </div>
    );
}

/**
 * catscreen-7a2c4e91 Stage 4 — a dialog that fits a phone: a sheet from the bottom on a narrow
 * screen, a centred box on a wide one; scrolls inside itself; Escape closes it unless `busy`.
 */
export function Dialog({ open, title, onClose, busy, children, footer, wide }) {
    React.useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape' && !busy && onClose) onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, busy, onClose]);
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center sm:p-4" role="dialog" aria-modal="true" aria-labelledby="cat-dialog-title">
            <div className={`bg-white w-full ${wide ? 'sm:max-w-2xl' : 'sm:max-w-lg'} max-h-[92vh] flex flex-col rounded-t-2xl sm:rounded-xl shadow-xl`}>
                <div className="flex items-center justify-between gap-2 px-4 sm:px-5 py-2 border-b border-gray-200">
                    <h2 id="cat-dialog-title" className="text-lg font-semibold text-gray-800 truncate">{title}</h2>
                    {onClose && (
                        <button type="button" className="w-11 h-11 inline-flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40" aria-label="Close" onClick={onClose} disabled={busy}>
                            <X size={18} />
                        </button>
                    )}
                </div>
                <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-4">{children}</div>
                {footer && <div className="px-4 sm:px-5 py-3 border-t border-gray-200 flex flex-wrap justify-end gap-2">{footer}</div>}
            </div>
        </div>
    );
}

/** The orange "type CHANGE" box for a change over 500 items. */
// The reassurance under the orange line, per kind of change.
export const BIG_CHANGE_NOTE = {
    price: "Each price change goes into that item's price history. Estimates and invoices already written don't change.",
    archive: "Archived items stay in your catalog and can be restored. Estimates and invoices already written don't change.",
    folder: "Only folders change — prices, estimates and invoices stay exactly as they are.",
};
export function TypedConfirm({ line, note, value, onChange, id = 'cat-typed-confirm' }) {
    return (
        <div className="p-3 rounded-lg border border-orange-300 bg-orange-50 space-y-2">
            <p className="text-sm font-semibold text-orange-900">{line}</p>
            <p className="text-sm text-orange-900">{note || BIG_CHANGE_NOTE.price}</p>
            <label htmlFor={id} className="block text-sm font-medium text-orange-900">Type CHANGE to confirm</label>
            <input id={id} className={INPUT} autoComplete="off" autoCapitalize="characters" spellCheck={false}
                value={value} onChange={(e) => onChange(e.target.value)} placeholder="CHANGE" />
        </div>
    );
}

/** "Discard your changes?" — its own small dialog so both buttons are full-size on a phone. */
export function DiscardDialog({ open, itemName, onKeep, onDiscard }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="cat-discard-title">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5 space-y-4">
                <h2 id="cat-discard-title" className="text-lg font-semibold text-gray-800">Discard your changes?</h2>
                <p className="text-sm text-gray-600">{itemName ? `Your changes to “${itemName}” haven't been saved.` : "Your changes haven't been saved."}</p>
                <div className="flex flex-wrap justify-end gap-2">
                    <button type="button" className={BTN_GHOST} onClick={onKeep} autoFocus>Keep editing</button>
                    <button type="button" className={BTN_DANGER} onClick={onDiscard}>Discard changes</button>
                </div>
            </div>
        </div>
    );
}

export function TypeBadge({ type }) {
    if (!type) return <span className="text-xs text-gray-400">—</span>;
    const tone = type === 'Service' ? 'bg-blue-50 text-blue-700 border-blue-200'
        : type === 'Part' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-orange-50 text-orange-700 border-orange-200';
    return <span className={`inline-block text-xs px-2 py-0.5 rounded-full border font-semibold ${tone}`}>{type}</span>;
}
